import {
  call,
  put,
  delay,
  select,
  takeEvery,
  takeLeading,
  takeLatest,
} from 'redux-saga/effects'
import { Graphic, GraphicDTO } from '@graphics/gfx-common/dist/types'
import {
  types,
  ICreatePackAction,
  IGetPackByIdAction,
  IDeletePackAction,
  IUpdatePackAction,
  IUpdatePackPollingSuccessAction,
  IDeleteFileAction,
  IDeleteFilePollingSuccessAction,
  IPublishPackAction,
  ICheckPackBeingPublishedAction,
  ICreatePackFromFileSuccess,
  ICreatePackFromFile,
  IValidateSlugAction,
  IPublishPackPollingSuccessAction,
  IDeletePackPollingSuccessAction,
  ICreatePackFromDataStreamURI,
  PackType,
} from '../types/pack'
import { Modals } from '../types/modal'
import { ITask } from '../types/polling'
import { push } from '../actions/router'
import * as actions from '../actions/pack'
import * as fileActions from '../actions/file'
import * as pollingActions from '../actions/polling'
import * as datachartActions from '../actions/datachart'
import { modalOpen } from '../actions/modal'
import * as selectors from '../selectors/pack'
import { trace } from '../services/common'
import * as services from '../services/pack'
import {
  validateSlugline,
  IValidateSluglineResponse,
  applyAdditionalTopicCodes,
} from '../services/typeahead'
import { messages } from '../config/messages'

export function* createPackSaga(action: ICreatePackAction) {
  try {
    const newPack: Graphic = yield call(services.createPack, action.packData)
    const packData: GraphicDTO = yield call(services.getPackById, newPack.id)
    yield put(
      actions.createPackSuccess(
        (packData as unknown) as PackType,
        messages.graphicCreateSuccess
      )
    )
    yield put(
      push(
        `/group/${packData.group}/pack/${packData.id}?edition=overall-pack&tab=upload`
      )
    )
  } catch (err) {
    trace(err)
    yield put(actions.createPackFailure(err.message, err.correlationId))
  }
}

export function* getPackSaga(action: IGetPackByIdAction) {
  try {
    const packOld: PackType = yield select(selectors.selectPack)
    const packData: GraphicDTO = yield call(
      services.getPackById,
      action.id,
      packOld && packOld.id === action.id && packOld.modified
    )
    if (!packData) {
      yield put(actions.getPackByIdNoUpdates())
    } else {
      yield put(actions.getPackByIdSuccess((packData as unknown) as PackType))
    }
  } catch (err) {
    trace(err)
    yield put(push(`/group/${action.group}/publish`))
    yield put(actions.getPackByIdFailure(err.message, err.correlationId))
  }
}

export function* deletePackSaga(action: IDeletePackAction) {
  try {
    const taskData = yield call(services.deletePack, action.id)
    const { location } = taskData
    yield put(
      pollingActions.dataPollingAction(
        location,
        Date.now(),
        actions.deletePackPollingSuccess,
        actions.deletePackFailure
      )
    )
  } catch (err) {
    trace(err)
    const { group }: PackType = yield select(selectors.selectPack)
    yield put(push(`/group/${group}/publish`))
    yield put(actions.deletePackFailure(err.message, err.correlationId))
  }
}

export function* deletePackPollingSuccessSaga(
  action: IDeletePackPollingSuccessAction
) {
  try {
    const { title, group }: PackType = yield select(selectors.selectPack)
    yield put(push(`/group/${group}/publish`))
    yield delay(500)
    yield put(
      actions.deletePackSuccess(
        messages.createPackDeleteSuccess(title || 'Untitled')
      )
    )
  } catch (err) {
    yield put(actions.deletePackFailure(err.message, err.correlationId))
  }
}

export function* updatePackSaga(action: IUpdatePackAction) {
  try {
    const { location }: ITask = yield call(services.updatePack, action.packData)
    yield put(
      pollingActions.dataPollingAction(
        location,
        Date.now(),
        actions.updatePackPollingSuccess,
        actions.updatePackFailure
      )
    )
  } catch (err) {
    trace(err)
    yield put(actions.updatePackFailure(err.message, err.correlationId))
  }
}

export function* updatePackPollingSuccessSaga(
  action: IUpdatePackPollingSuccessAction
) {
  try {
    const packId: string = yield select(selectors.selectPackId)
    const packData: GraphicDTO = yield call(services.getPackById, packId)
    yield put(
      actions.updatePackSuccess(
        (packData as unknown) as PackType,
        messages.graphicUpdateSuccess
      )
    )
  } catch (err) {
    trace(err)
    yield put(actions.updatePackFailure(err.message, err.correlationId))
  }
}

export function* validateSlugSaga(action: IValidateSlugAction) {
  try {
    const [slugWhithoutSlash, wildSlug = ''] = action.slug.split('/')
    const {
      passed,
      suggestedEvent,
      event,
      messages: slugMessages,
    }: IValidateSluglineResponse = yield call(
      validateSlugline(action.language),
      slugWhithoutSlash
    )
    if (!passed && !!suggestedEvent) {
      yield put(
        modalOpen(Modals.slugValidationModal, {
          changeSlugCallback: () => {
            action.changeSlugCallback(suggestedEvent.slugline + wildSlug)
            if (!!action.changeTopicsCodesCallback) {
              applyAdditionalTopicCodes(
                suggestedEvent,
                action.changeTopicsCodesCallback
              )
            }
          },
          message: !!slugMessages[0] ? slugMessages[0] : '',
        })
      )
    } else if (!!event && !!action.changeTopicsCodesCallback) {
      applyAdditionalTopicCodes(event, action.changeTopicsCodesCallback)
    }
    yield put(actions.validateSlugFinished())
  } catch (err) {
    trace(err)
  } finally {
    if (!!action.onValidate) {
      action.onValidate()
    }
  }
}

export function* publishPackSaga(action: IPublishPackAction) {
  try {
    const { location }: ITask = yield call(services.updatePack, action.packData)
    yield put(
      pollingActions.dataPollingAction(
        location,
        Date.now(),
        actions.publishPackPollingSuccess,
        actions.publishPackFailure
      )
    )
  } catch (err) {
    trace(err)
    yield put(actions.publishPackFailure(err.message, err.correlationId))
  }
}

export function* deleteFileSaga(action: IDeleteFileAction) {
  try {
    const { location }: ITask = yield call(
      services.deleteFile,
      action.packId,
      action.fileId
    )
    yield put(
      pollingActions.dataPollingAction(
        location,
        Date.now(),
        actions.deleteFilePollingSuccess,
        actions.deleteFileFailure,
        { fileId: action.fileId }
      )
    )
  } catch (err) {
    trace(err)
    yield put(actions.deleteFileFailure(err.message, err.correlationId))
  }
}

export function* deleteFilePollingSuccessSaga(
  action: IDeleteFilePollingSuccessAction
) {
  try {
    const packId: string = yield select(selectors.selectPackId)
    const groupId: string = yield select(selectors.selectParentGroup)
    yield put(actions.getPackById(packId))
    yield put(
      push(`/group/${groupId}/pack/${packId}?edition=overall-pack&tab=details`)
    )
    const { editionName } = yield select(
      selectors.selectEdition(action.data.editionId || '')
    )
    yield delay(500)
    yield put(
      actions.deleteFileSuccess(messages.createFileDeleteSuccess(editionName))
    )
  } catch (err) {
    trace(err)
    yield put(actions.deleteFileFailure(err.message, err.correlationId))
  }
}

export function* publishPackPollingSuccessSaga(
  action: IPublishPackPollingSuccessAction
) {
  try {
    const packId: string = yield select(selectors.selectPackId)
    const packData: GraphicDTO = yield call(services.getPackById, packId)
    yield put(
      push(
        `/group/${packData.group}/pack/${packData.id}?edition=overall-pack&tab=details`
      )
    )
    yield put(actions.publishPackSuccess((packData as unknown) as PackType))
  } catch (err) {
    trace(err)
    yield put(actions.publishPackFailure(err.message, err.correlationId))
  }
}

export function* checkPackBeingPublishedSaga(
  action: ICheckPackBeingPublishedAction
) {
  try {
    yield delay(10000)
    const packId: string = yield select(selectors.selectPackId)
    if (!packId) {
      return
    }
    // We shoud get modified value from previous iteration
    yield put(actions.getPackByIdShadow(packId))
    const packData: PackType = yield select(selectors.selectPack)
    if (
      packData === null ||
      (packData.publishing && packData.publishing.publishing)
    ) {
      yield put(action)
    } else {
      yield put(actions.publishPackSuccess(packData))
    }
  } catch (err) {
    trace(err)
    yield put(actions.publishPackFailure(err.message, err.correlationId))
  }
}

export function* createPackFromFileSaga(action: ICreatePackFromFile) {
  try {
    const { location }: ITask = yield call(
      services.createPackFromFile,
      action.groupId,
      action.fileId
    )
    yield put(
      pollingActions.dataPollingAction(
        location,
        Date.now(),
        actions.createPackFromFileSuccess(action.groupId),
        fileActions.createOrUpdateEditionFailure
      )
    )
  } catch (err) {
    trace(err)
    yield put(actions.createPackFailure(err.message, err.correlationId))
    yield put(
      fileActions.createOrUpdateEditionFailure(err.message, err.correlationId)
    )
  }
}

export function* createPackFromDataStreamURISaga(
  action: ICreatePackFromDataStreamURI
) {
  try {
    yield put(datachartActions.createDataChartShowProgress())
    const { location } = yield call(
      services.createPackFromDataStreamURI,
      action.groupId,
      action.dataStreamURI
    )

    yield put(
      pollingActions.dataPollingAction(
        location,
        Date.now(),
        actions.createPackFromFileSuccess(action.groupId),
        datachartActions.createDataChartFailure
      )
    )
  } catch (err) {
    trace(err)
    yield put(actions.createPackFailure(err.message, err.correlationId))
    yield put(datachartActions.createDataChartHideProgress())
  }
}

export function* createPackFromFileSuccessSaga(
  action: ICreatePackFromFileSuccess
) {
  const packId = action.data.resource && action.data.resource.split('/').pop()
  yield delay(1000)
  yield put(fileActions.closeUploadDialog())
  yield put(fileActions.resetFileData())
  yield put(
    push(
      `/group/${action.groupId}/pack/${packId}?tab=publish&edition=overall-pack`
    )
  )
  yield put(datachartActions.createDataChartHideProgress())
}

export function* packWatcher() {
  yield takeLeading(types.CREATE_PACK__STARTED, createPackSaga)
  yield takeEvery(types.GET_PACK_BY_ID__STARTED, getPackSaga)
  yield takeEvery(types.GET_PACK_BY_ID__STARTED_SHADOW, getPackSaga)
  yield takeLeading(types.DELETE_PACK__STARTED, deletePackSaga)
  yield takeLeading(types.UPDATE_PACK__STARTED, updatePackSaga)
  yield takeLatest(
    types.UPDATE_PACK_POLLING__SUCCESS,
    updatePackPollingSuccessSaga
  )
  yield takeLeading(types.VALIDATE_SLUG__STARTED, validateSlugSaga)
  yield takeEvery(types.DELETE_FILE__STARTED, deleteFileSaga)
  yield takeEvery(
    types.DELETE_FILE_POLLING__SUCCESS,
    deleteFilePollingSuccessSaga
  )
  yield takeLeading(types.PUBLISH_PACK__STARTED, publishPackSaga)
  yield takeLatest(
    types.PUBLISH_PACK_POLLING__SUCCESS,
    publishPackPollingSuccessSaga
  )
  yield takeEvery(types.CHECK_PACK_BEING_PUBLISHED, checkPackBeingPublishedSaga)
  yield takeEvery(
    types.DELETE_PACK_POLLING__SUCCESS,
    deletePackPollingSuccessSaga
  )
  yield takeLatest(
    types.CREATE_PACK_FROM_FILE__SUCCESS,
    createPackFromFileSuccessSaga
  )
  yield takeLatest(types.CREATE_PACK_FROM_FILE__STARTED, createPackFromFileSaga)
  yield takeLatest(
    types.CREATE_PACK_FROM_DATASTREAMURI__STARTED,
    createPackFromDataStreamURISaga
  )
}
